import { Component, lazy, Suspense, StrictMode, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./styles.css";
import "./refinement.css";
import "./components/StoryWorkspace.css";
import "./gameplay-refinements.css";
import "./studio-restoration.css";
import { AppAppearanceProvider } from "./runtime/AppAppearance";
import { LiquidBackground } from "./components/LiquidBackground";
import { runtimeConfig } from "./config/runtimeConfig";

const LocalUiPreview = import.meta.env.DEV && import.meta.env.MODE === "local-ui" ? lazy(() => import("./dev/LocalUiPreview").then(module => ({ default: module.LocalUiPreview }))) : null;

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class RootErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Root render error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--pf-background, #121012)",
          color: "var(--pf-foreground, #eee8eb)",
          padding: "24px",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, sans-serif"
        }}>
          <h2 style={{ color: "var(--pf-primary)", marginBottom: "12px" }}>เกิดข้อผิดพลาดในการโหลดระบบ</h2>
          <p style={{ color: "var(--pf-muted-foreground)", maxWidth: "480px", marginBottom: "20px", fontSize: "14px" }}>
            อาจมีแคชไฟล์เวอร์ชันเก่าตกค้างในเบราว์เซอร์ของคุณ กดปุ่มด้านล่างเพื่อล้างแคชและโหลดใหม่
          </p>
          <pre style={{
            background: "var(--pf-surface)",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "12px",
            color: "var(--pf-destructive)",
            maxWidth: "90vw",
            overflowX: "auto",
            marginBottom: "20px"
          }}>
            {this.state.error?.message || "Unknown error"}
          </pre>
          <button
            onClick={() => {
              if (typeof caches !== "undefined") {
                caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).finally(() => {
                  globalThis.location.reload();
                });
              } else {
                globalThis.location.reload();
              }
            }}
            style={{
              background: "var(--pf-primary)",
              border: "none",
              color: "var(--pf-primary-foreground)",
              fontWeight: 600,
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            ล้างแคชและโหลดใหม่ (Reload)
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootErrorBoundary>
      <AppAppearanceProvider>
        <LiquidBackground />
        {runtimeConfig.localUiOnly && ["localhost", "127.0.0.1", "[::1]"].includes(location.hostname) && location.pathname === "/__ui/layout" && LocalUiPreview ? <Suspense fallback={<p>กำลังเปิดตัวอย่าง…</p>}><LocalUiPreview /></Suspense> : <App />}
      </AppAppearanceProvider>
    </RootErrorBoundary>
  </StrictMode>,
);

window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      void registration.unregister();
    }
  });
}
